/**
 * The template draft, used by Create and Edit, and every rule applied to it. Pure so the form, the
 * summary panel and the tests share one definition; the company service applies the same rules on save.
 */

import {WORKING_DAYS_PER_WEEK, weeksForDays} from '../baselineTemplateFormat';

export {WORKING_DAYS_PER_WEEK, weeksForDays};
export const DEFAULT_PHASE_DURATION_DAYS = 20;
export const NAME_MAX_LENGTH = 191;
// Enforced by the description's maxLength; the server refuses anything longer.
export const DESCRIPTION_MAX_LENGTH = 5000;
export const DELIVERABLE_NAME_MAX_LENGTH = 300;
export const DELIVERABLE_DESCRIPTION_MAX_LENGTH = DESCRIPTION_MAX_LENGTH;
export const START_WEEK_MAX = 520;
export const DURATION_DAYS_MAX = 2600;
export const HOURS_MAX = 10000;

export type Option = {id: string; label: string};

export type TimeEstimateDraft = {key: string; roleId: string | null; hours: number | null; deliverableKey: string | null};

export type ExpenseEstimateDraft = {
	key: string;
	name: string;
	expenseCategoryId: string | null;
	revenue: number | null;
	cost: number | null;
	/** One of the phase's deliverables, or null at phase level. */
	deliverableKey: string | null;
};

export type DeliverableDraft = {key: string; name: string; description: string};

export type PhaseDraft = {
	key: string;
	name: string;
	startWeek: number | null;
	durationDays: number | null;
	/** The template deliverables on this phase, in card order. */
	deliverableKeys: string[];
	timeEstimates: TimeEstimateDraft[];
	expenseEstimates: ExpenseEstimateDraft[];
};

export type TemplateDraft = {
	name: string;
	description: string;
	includeDeliverables: boolean;
	/** Shared by the phases that link them. One linked to no phase is not saved. */
	deliverables: DeliverableDraft[];
	phases: PhaseDraft[];
};

/** Field path → translation id, in on-screen order, so the first key is the field to scroll to. */
export type FieldErrors = Record<string, string>;

export type BaselineTemplateRequest = {
	name: string;
	description: string | null;
	includeDeliverables: boolean;
	deliverables: {key: string; name: string; description: string | null}[];
	phases: {
		name: string;
		startWeek: number;
		durationDays: number;
		deliverableKeys: string[];
		timeEstimates: {roleId: string; minutes: number; deliverableKey: string | null}[];
		expenseEstimates: {
			name: string;
			expenseCategoryId: string;
			revenue: number;
			cost: number;
			deliverableKey: string | null;
		}[];
	}[];
};

let keySeed = 0;
const nextKey = (prefix: string): string => {
	keySeed += 1;
	return `${prefix}-${keySeed}`;
};

export const toNullableNumber = (value: number | null | undefined): number | null =>
	value === null || value === undefined || Number.isNaN(value) ? null : value;

export const blankTimeEstimate = (deliverableKey: string | null = null): TimeEstimateDraft => ({
	key: nextKey('time'),
	roleId: null,
	hours: null,
	deliverableKey,
});

export const blankExpenseEstimate = (deliverableKey: string | null = null): ExpenseEstimateDraft => ({
	key: nextKey('expense'),
	name: '',
	expenseCategoryId: null,
	revenue: null,
	cost: null,
	deliverableKey,
});

/** With deliverables on, a new phase has no rows: lines are added inside a deliverable card. */
export const blankPhase = (startWeek: number, includeDeliverables = false): PhaseDraft => ({
	key: nextKey('phase'),
	name: '',
	startWeek,
	durationDays: DEFAULT_PHASE_DURATION_DAYS,
	deliverableKeys: [],
	timeEstimates: includeDeliverables ? [] : [blankTimeEstimate()],
	expenseEstimates: [],
});

export const blankTemplate = (): TemplateDraft => ({
	name: '',
	description: '',
	includeDeliverables: false,
	deliverables: [],
	phases: [blankPhase(1)],
});

const isBlank = (value: string): boolean => value.trim() === '';

const isWholeInRange = (value: number | null, max: number): value is number =>
	value !== null && Number.isInteger(value) && value >= 1 && value <= max;

export const phaseEndDay = (phase: PhaseDraft): number =>
	isWholeInRange(phase.startWeek, START_WEEK_MAX) && isWholeInRange(phase.durationDays, DURATION_DAYS_MAX)
		? (phase.startWeek - 1) * WORKING_DAYS_PER_WEEK + phase.durationDays
		: 0;

export const templateDurationDays = (draft: TemplateDraft): number => Math.max(0, ...draft.phases.map(phaseEndDay));

export const nextPhaseStartWeek = (draft: TemplateDraft): number =>
	Math.ceil((templateDurationDays(draft) + 1) / WORKING_DAYS_PER_WEEK);

export const addPhase = (draft: TemplateDraft): TemplateDraft => ({
	...draft,
	phases: [...draft.phases, blankPhase(nextPhaseStartWeek(draft), draft.includeDeliverables)],
});

export const removePhase = (draft: TemplateDraft, phaseKey: string): TemplateDraft =>
	draft.phases.length <= 1 ? draft : {...draft, phases: draft.phases.filter(phase => phase.key !== phaseKey)};

export const updatePhase = (draft: TemplateDraft, phaseKey: string, patch: Partial<PhaseDraft>): TemplateDraft => ({
	...draft,
	phases: draft.phases.map(phase => (phase.key === phaseKey ? {...phase, ...patch} : phase)),
});

const mapPhase = (draft: TemplateDraft, phaseKey: string, update: (phase: PhaseDraft) => PhaseDraft): TemplateDraft => ({
	...draft,
	phases: draft.phases.map(phase => (phase.key === phaseKey ? update(phase) : phase)),
});

type KeyedRow = {deliverableKey: string | null};

/** A phase's rows in one deliverable card, or with `deliverableKey` null, its phase-level rows. */
export const rowsFor = <T extends KeyedRow>(rows: T[], deliverableKey: string | null): T[] =>
	rows.filter(row => row.deliverableKey === deliverableKey);

/**
 * Puts one card's edited rows back among the phase's rows, each in its old place, with new rows at the end. Keeping
 * the order means an edit undone by hand is not dirty.
 */
export const replaceRowsFor = <T extends KeyedRow>(rows: T[], deliverableKey: string | null, cardRows: T[]): T[] => {
	const queue = [...cardRows];
	const kept = rows.flatMap(row => {
		if (row.deliverableKey !== deliverableKey) {
			return [row];
		}
		const next = queue.shift();
		return next ? [next] : [];
	});
	return [...kept, ...queue];
};

const moveRows = <T extends KeyedRow>(rows: T[], from: string | null, to: string): T[] =>
	rows.map(row => (row.deliverableKey === from ? {...row, deliverableKey: to} : row));

/** The template's deliverables that a phase links: the ones a save keeps. */
export const linkedDeliverables = (draft: TemplateDraft): DeliverableDraft[] => {
	const linked = new Set(draft.phases.flatMap(phase => phase.deliverableKeys));
	return draft.deliverables.filter(deliverable => linked.has(deliverable.key));
};

export const linkDeliverable = (draft: TemplateDraft, phaseKey: string, deliverableKey: string): TemplateDraft =>
	mapPhase(draft, phaseKey, phase =>
		phase.deliverableKeys.includes(deliverableKey)
			? phase
			: {...phase, deliverableKeys: [...phase.deliverableKeys, deliverableKey]}
	);

/** A new deliverable for the whole template, linked to the end of one phase. */
export const addDeliverableToPhase = (
	draft: TemplateDraft,
	phaseKey: string,
	fields: {name: string; description: string}
): TemplateDraft => {
	const deliverable: DeliverableDraft = {key: nextKey('deliverable'), ...fields};
	return linkDeliverable({...draft, deliverables: [...draft.deliverables, deliverable]}, phaseKey, deliverable.key);
};

/**
 * Swaps one phase's deliverable for another, in place, and moves its lines along. `from` null is the phase's
 * phase-level lines. When `to` is already on the phase, the lines join it and the old link goes. Changing a
 * deliverable to itself changes nothing.
 */
export const changeDeliverable = (draft: TemplateDraft, phaseKey: string, from: string | null, to: string): TemplateDraft =>
	from === to
		? draft
		: mapPhase(draft, phaseKey, phase => {
				let deliverableKeys: string[];
				if (phase.deliverableKeys.includes(to)) {
					deliverableKeys = phase.deliverableKeys.filter(key => key !== from);
				} else if (from === null) {
					deliverableKeys = [...phase.deliverableKeys, to];
				} else {
					deliverableKeys = phase.deliverableKeys.map(key => (key === from ? to : key));
				}
				return {
					...phase,
					deliverableKeys,
					timeEstimates: moveRows(phase.timeEstimates, from, to),
					expenseEstimates: moveRows(phase.expenseEstimates, from, to),
				};
		  });

/** Unlinks a deliverable from one phase and deletes its lines there. Other phases keep it. */
export const removeDeliverableFromPhase = (draft: TemplateDraft, phaseKey: string, deliverableKey: string): TemplateDraft =>
	mapPhase(draft, phaseKey, phase => ({
		...phase,
		deliverableKeys: phase.deliverableKeys.filter(key => key !== deliverableKey),
		timeEstimates: phase.timeEstimates.filter(row => row.deliverableKey !== deliverableKey),
		expenseEstimates: phase.expenseEstimates.filter(row => row.deliverableKey !== deliverableKey),
	}));

/** Changes a deliverable's name and description in one place, so every phase it sits on shows the change. */
export const editDeliverable = (
	draft: TemplateDraft,
	deliverableKey: string,
	fields: {name: string; description: string}
): TemplateDraft => ({
	...draft,
	deliverables: draft.deliverables.map(deliverable =>
		deliverable.key === deliverableKey ? {...deliverable, ...fields} : deliverable
	),
});

/** Whether turning deliverables off would change anything, so the form asks first. */
export const hasDeliverableContent = (draft: TemplateDraft): boolean =>
	draft.phases.some(phase => phase.deliverableKeys.length > 0);

const isFiniteNumber = (value: number | null): value is number => value !== null && Number.isFinite(value);

/** A missing, non-finite or zero amount counts as not entered. */
const isEmptyAmount = (value: number | null): boolean => !isFiniteNumber(value) || value === 0;

const MINUTES_PER_HOUR = 60;

/** Whole minutes as saved for a row; a missing or non-finite value saves nothing. */
const savedMinutes = (hours: number | null): number => (isFiniteNumber(hours) ? Math.round(hours * MINUTES_PER_HOUR) : 0);

export const isUntouchedTimeEstimate = (row: TimeEstimateDraft): boolean => row.roleId === null && isEmptyAmount(row.hours);

export const isUntouchedExpenseEstimate = (row: ExpenseEstimateDraft): boolean =>
	isBlank(row.name) && row.expenseCategoryId === null && isEmptyAmount(row.revenue) && isEmptyAmount(row.cost);

/**
 * Off: every line moves to phase level, and the deliverables and links go; no line is deleted. On: lines stay at
 * phase level, where each phase shows them in its No deliverable card, and rows nobody filled in are dropped, so
 * a phase with none shows only its Select Deliverable card.
 */
export const setIncludeDeliverables = (draft: TemplateDraft, includeDeliverables: boolean): TemplateDraft =>
	includeDeliverables
		? {
				...draft,
				includeDeliverables: true,
				phases: draft.phases.map(phase => ({
					...phase,
					timeEstimates: phase.timeEstimates.filter(row => !isUntouchedTimeEstimate(row)),
					expenseEstimates: phase.expenseEstimates.filter(row => !isUntouchedExpenseEstimate(row)),
				})),
		  }
		: {
				...draft,
				includeDeliverables: false,
				deliverables: [],
				phases: draft.phases.map(phase => ({
					...phase,
					deliverableKeys: [],
					timeEstimates: phase.timeEstimates.map(row => ({...row, deliverableKey: null})),
					expenseEstimates: phase.expenseEstimates.map(row => ({...row, deliverableKey: null})),
				})),
		  };

const phaseMinutes = (phase: PhaseDraft): number => phase.timeEstimates.reduce((sum, row) => sum + savedMinutes(row.hours), 0);

/** Hours as they will be saved: each row rounds to whole minutes first, then the minutes are summed. */
export const phaseHours = (phase: PhaseDraft): number => phaseMinutes(phase) / MINUTES_PER_HOUR;

export const totalHours = (draft: TemplateDraft): number =>
	draft.phases.reduce((sum, phase) => sum + phaseMinutes(phase), 0) / MINUTES_PER_HOUR;

export const deliverableCount = (draft: TemplateDraft): number | null =>
	draft.includeDeliverables ? linkedDeliverables(draft).length : null;

/** Filled-in rows only: untouched ones are not saved. */
export const timeEstimateCount = (rows: TimeEstimateDraft[]): number =>
	rows.filter(row => !isUntouchedTimeEstimate(row)).length;

/** Filled-in rows only: untouched ones are not saved. */
export const expenseEstimateCount = (rows: ExpenseEstimateDraft[]): number =>
	rows.filter(row => !isUntouchedExpenseEstimate(row)).length;

export const expenseLineCount = (draft: TemplateDraft): number =>
	draft.phases.reduce((sum, phase) => sum + expenseEstimateCount(phase.expenseEstimates), 0);

/** Keys of the rows in a phase patch that are untouched again, so the form can drop their errors. */
export const untouchedRowKeys = (patch: Partial<PhaseDraft>): string[] => [
	...(patch.timeEstimates ?? []).filter(isUntouchedTimeEstimate).map(row => row.key),
	...(patch.expenseEstimates ?? []).filter(isUntouchedExpenseEstimate).map(row => row.key),
];

// Hours compare as the whole minutes they save as: the hours field shows two decimals and commits
// what it shows, so 61 minutes (1.0166...) and a re-committed 1.02 are the same stored value.
const comparableHours = (hours: number | null): number | null =>
	isFiniteNumber(hours) ? Math.round(hours * MINUTES_PER_HOUR) : hours;

// Row keys are React bookkeeping, not content, so they are left out of the comparison. Deliverable keys are content:
// they say which deliverable a line belongs to. Only linked deliverables and filled-in rows count, as only they are
// saved: turning deliverables on drops a blank row that turning them off does not bring back.
const comparable = (draft: TemplateDraft) => ({
	name: draft.name,
	description: draft.description,
	includeDeliverables: draft.includeDeliverables,
	deliverables: linkedDeliverables(draft).map(({key, name, description}) => ({key, name, description})),
	phases: draft.phases.map(phase => ({
		name: phase.name,
		startWeek: phase.startWeek,
		durationDays: phase.durationDays,
		deliverableKeys: phase.deliverableKeys,
		timeEstimates: phase.timeEstimates
			.filter(row => !isUntouchedTimeEstimate(row))
			.map(row => ({
				roleId: row.roleId,
				hours: comparableHours(row.hours),
				deliverableKey: row.deliverableKey,
			})),
		expenseEstimates: phase.expenseEstimates
			.filter(row => !isUntouchedExpenseEstimate(row))
			.map(row => ({
				name: row.name,
				expenseCategoryId: row.expenseCategoryId,
				revenue: row.revenue,
				cost: row.cost,
				deliverableKey: row.deliverableKey,
			})),
	})),
});

const snapshot = (draft: TemplateDraft): string => JSON.stringify(comparable(draft));

let blankSnapshot: string | null = null;

/**
 * Compares field by field with the template as it was loaded, or with a blank template when there is none,
 * so an edit undone by hand is not dirty.
 */
export const isDirty = (draft: TemplateDraft, initial?: TemplateDraft): boolean => {
	if (initial) {
		return snapshot(draft) !== snapshot(initial);
	}
	// Built once on first use: blankTemplate() hands out row keys, and a check must not use any up.
	blankSnapshot ??= snapshot(blankTemplate());
	return snapshot(draft) !== blankSnapshot;
};

export const isNameTaken = (name: string, existingNames: readonly string[]): boolean => {
	const wanted = name.trim().toLowerCase();
	return wanted !== '' && existingNames.some(existing => existing.trim().toLowerCase() === wanted);
};

/** The translation id of the name's first problem, or undefined. Shared by the form and the duplicate prompt. */
export const nameError = (name: string, existingNames: readonly string[]): string | undefined => {
	const trimmed = name.trim();
	if (trimmed === '') {
		return 'baseline_templates.errors.name_required';
	}
	if (trimmed.length > NAME_MAX_LENGTH) {
		return 'baseline_templates.errors.name_too_long';
	}
	return isNameTaken(trimmed, existingNames) ? 'baseline_templates.errors.name_taken' : undefined;
};

const validateTimeEstimate = (row: TimeEstimateDraft, errors: FieldErrors): void => {
	if (isUntouchedTimeEstimate(row)) {
		return;
	}
	if (row.roleId === null) {
		errors[`${row.key}.roleId`] = 'deliverable.select_role';
	}
	if (!isFiniteNumber(row.hours) || row.hours < 0) {
		if (row.roleId !== null) {
			errors[`${row.key}.hours`] = 'baseline_templates.errors.enter_hours';
		}
	} else if (row.hours > HOURS_MAX) {
		errors[`${row.key}.hours`] = 'baseline_templates.errors.hours_too_high';
	}
};

const validateExpenseEstimate = (row: ExpenseEstimateDraft, errors: FieldErrors): void => {
	if (isUntouchedExpenseEstimate(row)) {
		return;
	}
	if (isBlank(row.name)) {
		errors[`${row.key}.name`] = 'baseline_templates.errors.expense_name_required';
	} else if (row.name.trim().length > NAME_MAX_LENGTH) {
		errors[`${row.key}.name`] = 'baseline_templates.errors.name_too_long';
	}
	if (row.expenseCategoryId === null) {
		errors[`${row.key}.expenseCategoryId`] = 'deliverable.select_expense_category';
	}
};

const validatePhase = (phase: PhaseDraft, errors: FieldErrors): void => {
	if (isBlank(phase.name)) {
		errors[`${phase.key}.name`] = 'baseline_templates.errors.phase_name_required';
	} else if (phase.name.trim().length > NAME_MAX_LENGTH) {
		errors[`${phase.key}.name`] = 'baseline_templates.errors.name_too_long';
	}
	if (!isWholeInRange(phase.startWeek, START_WEEK_MAX)) {
		errors[`${phase.key}.startWeek`] = 'baseline_templates.errors.start_week_invalid';
	}
	if (!isWholeInRange(phase.durationDays, DURATION_DAYS_MAX)) {
		errors[`${phase.key}.durationDays`] = 'baseline_templates.errors.duration_invalid';
	}
	phase.timeEstimates.forEach(row => validateTimeEstimate(row, errors));
	phase.expenseEstimates.forEach(row => validateExpenseEstimate(row, errors));
};

export const validate = (draft: TemplateDraft, existingNames: readonly string[]): FieldErrors => {
	const errors: FieldErrors = {};
	const nameProblem = nameError(draft.name, existingNames);
	if (nameProblem) {
		errors.name = nameProblem;
	}
	draft.phases.forEach(phase => validatePhase(phase, errors));
	return errors;
};

/** Only call on a draft that `validate` accepted. */
export const normalise = (draft: TemplateDraft): BaselineTemplateRequest => {
	const includeDeliverables = draft.includeDeliverables;
	return {
		name: draft.name.trim(),
		description: isBlank(draft.description) ? null : draft.description.trim(),
		includeDeliverables,
		deliverables: includeDeliverables
			? linkedDeliverables(draft).map(deliverable => ({
					key: deliverable.key,
					name: deliverable.name.trim(),
					description: isBlank(deliverable.description) ? null : deliverable.description.trim(),
			  }))
			: [],
		phases: draft.phases.map(phase => ({
			name: phase.name.trim(),
			startWeek: phase.startWeek as number,
			durationDays: phase.durationDays as number,
			deliverableKeys: includeDeliverables ? phase.deliverableKeys : [],
			timeEstimates: phase.timeEstimates
				.filter(row => !isUntouchedTimeEstimate(row))
				.map(row => ({
					roleId: row.roleId as string,
					minutes: savedMinutes(row.hours),
					deliverableKey: includeDeliverables ? row.deliverableKey : null,
				})),
			expenseEstimates: phase.expenseEstimates
				.filter(row => !isUntouchedExpenseEstimate(row))
				.map(row => ({
					name: row.name.trim(),
					expenseCategoryId: row.expenseCategoryId as string,
					revenue: isFiniteNumber(row.revenue) ? row.revenue : 0,
					cost: isFiniteNumber(row.cost) ? row.cost : 0,
					deliverableKey: includeDeliverables ? row.deliverableKey : null,
				})),
		})),
	};
};

/** A stored template as the editor's query returns it: Relay's shape, everything nullable. */
export type TemplateDetail = {
	readonly name: string | null;
	readonly description: string | null;
	readonly includeDeliverables: boolean | null;
	readonly deliverables?: ReadonlyArray<{
		readonly id: string | null;
		readonly name: string | null;
		readonly description: string | null;
	} | null> | null;
	readonly phases: ReadonlyArray<{
		readonly name: string | null;
		readonly startWeek: number | null;
		readonly durationDays: number | null;
		readonly deliverableIds?: ReadonlyArray<string | null> | null;
		readonly timeEstimates: ReadonlyArray<{
			readonly roleId: string | null;
			readonly minutes: number | null;
			readonly deliverableId?: string | null;
		} | null> | null;
		readonly expenseEstimates: ReadonlyArray<{
			readonly name: string | null;
			readonly expenseCategoryId: string | null;
			readonly revenue: number | null;
			readonly cost: number | null;
			readonly deliverableId?: string | null;
		} | null> | null;
	} | null> | null;
};

/**
 * The draft the editor starts from: stored minutes become hours, every row gets a fresh key, and the detail's
 * deliverable ids are used as deliverable keys, which the server reads back as any other key.
 */
export const fromDetail = (detail: TemplateDetail): TemplateDraft => ({
	name: detail.name ?? '',
	description: detail.description ?? '',
	includeDeliverables: detail.includeDeliverables === true,
	deliverables: (detail.deliverables ?? []).flatMap(deliverable =>
		deliverable?.id ? [{key: deliverable.id, name: deliverable.name ?? '', description: deliverable.description ?? ''}] : []
	),
	phases: (detail.phases ?? []).flatMap(phase =>
		phase
			? [
					{
						key: nextKey('phase'),
						name: phase.name ?? '',
						startWeek: phase.startWeek,
						durationDays: phase.durationDays,
						deliverableKeys: (phase.deliverableIds ?? []).flatMap(id => (id ? [id] : [])),
						timeEstimates: (phase.timeEstimates ?? []).flatMap(row =>
							row
								? [
										{
											key: nextKey('time'),
											roleId: row.roleId,
											hours: row.minutes === null ? null : row.minutes / MINUTES_PER_HOUR,
											deliverableKey: row.deliverableId ?? null,
										},
								  ]
								: []
						),
						expenseEstimates: (phase.expenseEstimates ?? []).flatMap(row =>
							row
								? [
										{
											key: nextKey('expense'),
											name: row.name ?? '',
											expenseCategoryId: row.expenseCategoryId,
											revenue: row.revenue,
											cost: row.cost,
											deliverableKey: row.deliverableId ?? null,
										},
								  ]
								: []
						),
					},
			  ]
			: []
	),
});

type Held = Readonly<Record<string, Option>>;

const heldBy = (rows: ReadonlyArray<{key: string; id: string | null}>, disabled: readonly Option[]): Record<string, Option> => {
	const byId = new Map(disabled.map(option => [option.id, option]));
	const held: Record<string, Option> = {};
	rows.forEach(row => {
		const option = row.id === null ? undefined : byId.get(row.id);
		if (option) {
			held[row.key] = option;
		}
	});
	return held;
};

/** The disabled roles that loaded rows hold, keyed by row: a row may keep one, and only that row offers it. */
export const heldDisabledRoles = (draft: TemplateDraft, disabledRoles: readonly Option[]): Record<string, Option> =>
	heldBy(
		draft.phases.flatMap(phase => phase.timeEstimates.map(row => ({key: row.key, id: row.roleId}))),
		disabledRoles
	);

/** The disabled expense categories that loaded rows hold, keyed by row. */
export const heldDisabledCategories = (draft: TemplateDraft, disabledCategories: readonly Option[]): Record<string, Option> =>
	heldBy(
		draft.phases.flatMap(phase => phase.expenseEstimates.map(row => ({key: row.key, id: row.expenseCategoryId}))),
		disabledCategories
	);

/**
 * What a row's picker offers: every enabled option, then the disabled one this row held when loaded.
 * A row holding nothing gets `enabled` itself, so its picker sees the same array on every render.
 */
export const optionsForRow = (enabled: Option[], held: Held | undefined, rowKey: string): Option[] => {
	const own = held?.[rowKey];
	return own ? [...enabled, own] : enabled;
};

type NamedNode = {readonly id: string; readonly name: string | null; readonly disabled?: boolean | null};

/** Enabled options sorted by label, and disabled ones labelled by `disabledLabel` (e.g. "Designer (disabled)"). */
export const splitOptions = (
	nodes: ReadonlyArray<NamedNode | null | undefined>,
	disabledLabel: (name: string) => string
): {enabled: Option[]; disabled: Option[]} => {
	const present = nodes.filter((node): node is NamedNode => !!node);
	return {
		enabled: present
			.filter(node => !node.disabled)
			.map(node => ({id: node.id, label: node.name ?? ''}))
			.sort((a, b) => a.label.localeCompare(b.label)),
		disabled: present.filter(node => node.disabled).map(node => ({id: node.id, label: disabledLabel(node.name ?? '')})),
	};
};
